import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Loader, Text } from '@cloudflare/kumo'
import { WarningCircleIcon } from '@phosphor-icons/react'
import { useTranslation } from '../i18n'

export interface CallbackError {
  /** OAuth error code, e.g. `access_denied`. */
  code: string
  /** Human-readable reason from Auth0, or null when none was provided. */
  description: string | null
}

/** Error codes we render a localized message for; anything else passes the
 *  raw `error_description` through. `access_denied` is Auth0's own denial
 *  (enrolled-domain block); `not_provisioned` / `no_company` are minted by the
 *  app when it redirects an authenticated-but-unprovisioned user here. */
export const KNOWN_ERROR_CODES = new Set(['access_denied', 'not_provisioned', 'no_company'])

/**
 * Resolve an Auth0 callback error from the redirect. Auth0 sends errors as
 * `?error=...&error_description=...` on the callback URL; the auth0-react SDK
 * also surfaces them via `useAuth0().error` (an `OAuthError` carrying `error`
 * / `error_description`). Prefer the URL (it's the source of truth for the
 * redirect), then fall back to the SDK error. Returns null when there is no
 * error, so the normal success/redirect path proceeds.
 */
export function resolveCallbackError(
  params: URLSearchParams,
  auth0Error?: unknown,
): CallbackError | null {
  const clean = (v: string | null | undefined): string | null => {
    if (!v) return null
    const trimmed = v.trim()
    return trimmed ? trimmed.slice(0, 300) : null
  }

  const urlCode = params.get('error')
  if (urlCode) {
    return { code: urlCode, description: clean(params.get('error_description')) }
  }

  if (auth0Error && typeof auth0Error === 'object' && 'error' in auth0Error) {
    const e = auth0Error as { error?: unknown; error_description?: unknown }
    if (typeof e.error === 'string' && e.error) {
      return {
        code: e.error,
        description: typeof e.error_description === 'string' ? clean(e.error_description) : null,
      }
    }
  }

  return null
}

export function CallbackPage() {
  const { isAuthenticated, isLoading, error, loginWithRedirect, logout } = useAuth0()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [params] = useSearchParams()

  const callbackError = resolveCallbackError(params, error)

  useEffect(() => {
    if (!isLoading && isAuthenticated && !callbackError) {
      navigate('/my/wifi', { replace: true })
    }
  }, [isAuthenticated, isLoading, callbackError, navigate])

  if (callbackError) {
    // Localize codes we recognize; otherwise pass Auth0's own reason through
    // (rendered as escaped text — never HTML). Fall back to a generic line
    // when there is no description to show.
    const reason = KNOWN_ERROR_CODES.has(callbackError.code)
      ? t(`callback.error.${callbackError.code}`)
      : (callbackError.description ?? t('callback.error.generic'))

    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-kumo-base text-kumo-default p-6">
        <div className="max-w-140 w-full flex flex-col items-center text-center gap-4">
          <WarningCircleIcon size={48} className="text-kumo-danger" />
          <Text variant="heading2" as="h1">
            {t('callback.error.title')}
          </Text>
          <Text variant="secondary">{reason}</Text>
          <div className="flex gap-3">
            <Button
              variant="primary"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            >
              {t('callback.error.logout')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => void loginWithRedirect({ authorizationParams: { prompt: 'login' } })}
            >
              {t('callback.error.tryAnother')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-kumo-base text-kumo-default">
      <Loader size="lg" />
      <Text variant="secondary">{t('callback.processing')}</Text>
    </div>
  )
}
